import { useEffect, useState } from 'react';
import api from '../services/ApiService';

// ── Types ─────────────────────────────────────────────────────────────────────

type Trunk = {
  id: number;
  trunk_name: string;
  display_name: string;
  sip_host: string;
  sip_port: number;
  auth_username: string | null;
  auth_password: string | null;
  codecs: string;
  context: string;
  transport_protocol: string;
  pjsip_options: any;
  is_primary: number;
  is_active: number;
  tenant_id?: number | null;
  trunk_profile?: string;
  auth_mode?: string;
  assigned_tenant_ids?: number[];
  read_only?: boolean;
  created_at?: string;
};

type Tenant = { id: number; tenant_name?: string; name?: string; company_name?: string; subdomain?: string };

type PjsipOpts = {
  bind_address?: string;
  external_signaling_address?: string;
  external_signaling_port?: string;
  external_media_address?: string;
  local_net?: string;
  tls_method?: string;
  tls_verify_client?: boolean;
  tls_verify_server?: boolean;
  tls_cert_file?: string;
  tls_priv_key_file?: string;
  tls_ca_list_file?: string;
  tls_port?: string;
  direct_media?: boolean;
  rtp_symmetric?: boolean;
  force_rport?: boolean;
  rewrite_contact?: boolean;
  dtmf_mode?: string;
  media_encryption?: string;
  send_pai?: boolean;
  send_rpid?: boolean;
  trust_id_inbound?: boolean;
  trust_id_outbound?: boolean;
  outbound_proxy?: string;
  from_user?: string;
  timers?: string;
  qualify_frequency?: string;
  max_contacts?: string;
  remove_existing?: boolean;
  extra_contacts?: string;
  identify_by_ip?: string;
};

const DEFAULT_OPTS: PjsipOpts = {
  rtp_symmetric: true,
  force_rport: true,
  rewrite_contact: true,
  tls_verify_server: true,
  tls_method: 'tlsv1_2',
  tls_ca_list_file: '/etc/ssl/certs/ca-certificates.crt',
  dtmf_mode: 'rfc4733',
  media_encryption: 'no',
  qualify_frequency: '30',
  max_contacts: '1',
  remove_existing: true,
};

// ── Trunk deployment profiles (Zoom-style evaluation) — auto-fill on select ────
// Mirrors backend GET /api/trunk-profiles. Picking a profile pre-fills the form
// with the correct PJSIP settings for that carrier type.
const TRUNK_PROFILES: Record<string, {
  label: string; transport_protocol: string; auth_mode: string; codecs: string; pjsip_options: PjsipOpts;
}> = {
  generic: {
    label: 'Generic SIP', transport_protocol: 'udp', auth_mode: 'register', codecs: 'ulaw,alaw',
    pjsip_options: { rtp_symmetric: true, force_rport: true, rewrite_contact: true, dtmf_mode: 'rfc4733', media_encryption: 'no' },
  },
  zoom_byoc: {
    label: 'Zoom BYOC / Premise Peering', transport_protocol: 'tls', auth_mode: 'ip', codecs: 'ulaw,alaw',
    pjsip_options: {
      media_encryption: 'sdes', rtp_symmetric: true, force_rport: true, rewrite_contact: true,
      dtmf_mode: 'rfc4733', timers: 'yes', qualify_frequency: '60', trust_id_outbound: true, send_pai: true,
      tls_method: 'tlsv1_2', tls_port: '5061',
    },
  },
  twilio: {
    label: 'Twilio Elastic SIP Trunk', transport_protocol: 'udp', auth_mode: 'register', codecs: 'ulaw',
    pjsip_options: { rtp_symmetric: true, force_rport: true, rewrite_contact: true, dtmf_mode: 'rfc4733', media_encryption: 'no' },
  },
  custom: {
    label: 'Custom', transport_protocol: 'udp', auth_mode: 'register', codecs: 'ulaw,alaw',
    pjsip_options: {},
  },
};

const EMPTY_FORM = {
  trunk_name: '',
  display_name: '',
  sip_host: '',
  sip_port: '5060',
  auth_username: '',
  auth_password: '',
  codecs: 'ulaw,alaw',
  context: 'from-trunk',
  transport_protocol: 'udp',
  trunk_profile: 'generic',
  auth_mode: 'register',
  is_primary: false,
  is_active: true,
  pjsip_options: DEFAULT_OPTS as PjsipOpts,
};

const tenantName = (t: Tenant) => t.tenant_name || t.name || t.company_name || t.subdomain || `Tenant ${t.id}`;

// ── Small helpers ─────────────────────────────────────────────────────────────

const S = {
  label: { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '0.5px', marginBottom: 4 },
  input: {
    width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
    background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(100,116,139,0.3)',
    color: '#f1f5f9', outline: 'none', boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
    background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(100,116,139,0.3)',
    color: '#f1f5f9', outline: 'none', boxSizing: 'border-box' as const,
  },
  sectionHead: {
    fontSize: 11, fontWeight: 700, color: '#8b5cf6', textTransform: 'uppercase' as const,
    letterSpacing: '0.8px', marginBottom: 10, marginTop: 18, paddingBottom: 4,
    borderBottom: '1px solid rgba(139,92,246,0.2)',
  },
  hint: { fontSize: 11, color: '#475569', marginTop: 3 },
};

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={S.label}>{label}</div>
      {children}
      {hint && <div style={S.hint}>{hint}</div>}
    </label>
  );
}

function CheckRow({ label, checked, onChange, hint }: { label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ marginTop: 2, accentColor: '#8b5cf6' }} />
      <span>
        <span style={{ fontSize: 13, color: '#e2e8f0' }}>{label}</span>
        {hint && <div style={S.hint}>{hint}</div>}
      </span>
    </label>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function TrunksPage({ user }: any = {}) {
  const isSuperAdmin = !!(user?.role === 'super_admin' || user?.is_admin);

  const [trunks, setTrunks]     = useState<Trunk[]>([]);
  const [tenants, setTenants]   = useState<Tenant[]>([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState<any>(EMPTY_FORM);
  const [editing, setEditing]   = useState<Trunk | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'advanced'>('basic');
  const [msg, setMsg]           = useState<{ text: string; ok: boolean } | null>(null);
  const [syncing, setSyncing]   = useState(false);
  // Assignment modal
  const [assignFor, setAssignFor] = useState<Trunk | null>(null);
  const [assignIds, setAssignIds] = useState<number[]>([]);
  const [savingAssign, setSavingAssign] = useState(false);

  // Read-only view for tenant admins (backend already scopes to assigned trunks & strips secrets)
  const readOnly = !isSuperAdmin;
  // Super admin: hide a sensitive/external trunk (e.g. esbc2) from all other admins
  async function toggleRestricted(t: any, on: boolean) {
    if (!window.confirm((on ? 'Hide ' : 'Show ') + t.trunk_name + (on ? ' from everyone except super admins?' : ' to tenant and site admins again?'))) return;
    try {
      await (api as any).setTrunkSuperAdminOnly(t.id, on);
      setTrunks((prev: any[]) => prev.map((x: any) => (x.id === t.id ? { ...x, super_admin_only: on ? 1 : 0 } : x)));
    } catch (e: any) { alert(e?.response?.data?.error || 'Failed to update trunk visibility'); }
  }

  async function load() {
    setLoading(true);
    try {
      const t = await api.getTrunks();
      setTrunks(Array.isArray(t) ? t : []);
    } catch { setTrunks([]); }
    if (isSuperAdmin) {
      try { const tn = await api.getTenants(); setTenants(Array.isArray(tn) ? tn : []); }
      catch { setTenants([]); }
    }
    setLoading(false);
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  function flash(text: string, ok = true) {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 4000);
  }

  function parsedOpts(raw: any): PjsipOpts {
    if (!raw) return { ...DEFAULT_OPTS };
    if (typeof raw === 'string') { try { return { ...DEFAULT_OPTS, ...JSON.parse(raw) }; } catch { return { ...DEFAULT_OPTS }; } }
    return { ...DEFAULT_OPTS, ...raw };
  }

  // Apply a deployment profile template to the form
  function applyProfile(profileKey: string) {
    const p = TRUNK_PROFILES[profileKey];
    if (!p) { setForm((f: any) => ({ ...f, trunk_profile: profileKey })); return; }
    setForm((f: any) => ({
      ...f,
      trunk_profile: profileKey,
      auth_mode: p.auth_mode,
      transport_protocol: p.transport_protocol,
      codecs: p.codecs,
      sip_port: p.transport_protocol === 'tls' ? '5061' : (f.sip_port || '5060'),
      pjsip_options: { ...DEFAULT_OPTS, ...p.pjsip_options },
    }));
  }

  function startCreate() {
    setEditing(null);
    setForm({ ...EMPTY_FORM, pjsip_options: { ...DEFAULT_OPTS } });
    setActiveTab('basic');
    setShowForm(true);
  }

  function startEdit(t: Trunk) {
    setEditing(t);
    setForm({
      trunk_name: t.trunk_name,
      display_name: t.display_name || '',
      sip_host: t.sip_host,
      sip_port: String(t.sip_port || 5060),
      auth_username: t.auth_username || '',
      auth_password: '',
      codecs: t.codecs || 'ulaw,alaw',
      context: t.context || 'from-trunk',
      transport_protocol: t.transport_protocol || 'udp',
      trunk_profile: t.trunk_profile || 'generic',
      auth_mode: t.auth_mode || 'register',
      is_primary: !!t.is_primary,
      is_active: !!t.is_active,
      pjsip_options: parsedOpts(t.pjsip_options),
    });
    setActiveTab('basic');
    setShowForm(true);
  }

  function setOpt(key: keyof PjsipOpts, value: any) {
    setForm((f: any) => ({ ...f, pjsip_options: { ...f.pjsip_options, [key]: value } }));
  }

  async function save() {
    try {
      const payload = {
        ...form,
        sip_port: parseInt(form.sip_port, 10) || 5060,
        is_primary: form.is_primary ? 1 : 0,
        is_active: form.is_active ? 1 : 0,
        auth_password: form.auth_password || '',
        trunk_profile: form.trunk_profile || 'generic',
        auth_mode: form.auth_mode || 'register',
        pjsip_options: form.pjsip_options,
      };
      if (editing) {
        await api.updateTrunk(editing.id, payload);
        flash('Trunk updated — configuration provisioned');
      } else {
        await api.createTrunk(payload);
        flash('Trunk created — configuration provisioned');
      }
      setShowForm(false);
      load();
    } catch (e: any) {
      flash(e?.response?.data?.error || 'Failed to save trunk', false);
    }
  }

  async function remove(t: Trunk) {
    if (!window.confirm(`Delete trunk "${t.trunk_name}"? This will remove it from the system.`)) return;
    try {
      await api.deleteTrunk(t.id);
      flash('Trunk deleted');
      load();
    } catch (e: any) {
      flash(e?.response?.data?.error || 'Delete failed', false);
    }
  }

  async function syncAll() {
    setSyncing(true);
    try { flash((await api.asteriskSync()).message || 'Sync complete'); }
    catch (e: any) { flash(e?.response?.data?.error || 'Sync failed', false); }
    setSyncing(false);
  }

  // ── Tenant assignment ──────────────────────────────────────────────────────
  async function openAssign(t: Trunk) {
    setAssignFor(t);
    try { setAssignIds(await api.getTrunkTenants(t.id) || []); }
    catch { setAssignIds(t.assigned_tenant_ids || []); }
  }
  function toggleAssign(id: number) {
    setAssignIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  }
  async function saveAssign() {
    if (!assignFor) return;
    setSavingAssign(true);
    try {
      await api.setTrunkTenants(assignFor.id, assignIds);
      flash(`Trunk "${assignFor.trunk_name}" assigned to ${assignIds.length} tenant(s) — dialplan updated`);
      setAssignFor(null);
      load();
    } catch (e: any) {
      flash(e?.response?.data?.error || 'Failed to update assignments', false);
    }
    setSavingAssign(false);
  }

  const opts: PjsipOpts = form.pjsip_options || {};
  const isTls = form.transport_protocol === 'tls';
  const tenantById = (id: number) => tenants.find(t => t.id === id);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1050 }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9', marginBottom: 4 }}>
        Trunks {readOnly && <span style={{ fontSize: 11, color: '#a78bfa', fontWeight: 700, marginLeft: 8, verticalAlign: 'middle' }}>READ-ONLY</span>}
      </div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
        {readOnly
          ? 'Trunks assigned to your organisation. Managed by your platform administrator.'
          : 'Configure SIP/PJSIP trunks and assign them to tenants. Changes are provisioned automatically.'}
      </div>

      {msg && (
        <div style={{
          padding: '10px 14px', marginBottom: 14, borderRadius: 8,
          background: msg.ok ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
          border: `1px solid ${msg.ok ? '#10b981' : '#ef4444'}40`,
          color: msg.ok ? '#6ee7b7' : '#fca5a5', fontSize: 13,
        }}>
          {msg.text}
        </div>
      )}

      {!readOnly && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          <button className="admin-btn primary" onClick={startCreate}>+ Add Trunk</button>
          <button className="admin-btn" onClick={syncAll} disabled={syncing}>
            {syncing ? 'Syncing…' : '⟳ Sync All'}
          </button>
        </div>
      )}

      {/* ── Form (super admin only) ─────────────────────────────────────────── */}
      {!readOnly && showForm && (
        <div style={{
          background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(139,92,246,0.2)',
          borderRadius: 12, padding: 20, marginBottom: 24,
        }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9', marginBottom: 14 }}>
            {editing ? `Edit: ${editing.trunk_name}` : 'New Trunk'}
          </div>

          <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid rgba(100,116,139,0.2)' }}>
            {(['basic', 'advanced'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '7px 18px', borderRadius: '6px 6px 0 0',
                  background: activeTab === tab ? 'rgba(139,92,246,0.2)' : 'transparent',
                  border: 'none',
                  borderBottom: activeTab === tab ? '2px solid #8b5cf6' : '2px solid transparent',
                  color: activeTab === tab ? '#c4b5fd' : '#64748b',
                  fontWeight: activeTab === tab ? 700 : 400,
                  fontSize: 13, cursor: 'pointer', textTransform: 'capitalize',
                }}
              >
                {tab === 'basic' ? '⚙ Basic' : '🔧 PJSIP Advanced'}
              </button>
            ))}
          </div>

          {/* ── BASIC TAB ─────────────────────────────────────────────────── */}
          {activeTab === 'basic' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
              <Field label="Deployment Profile" hint="Auto-fills the correct PJSIP settings for the carrier type">
                <select style={S.select} value={form.trunk_profile}
                  onChange={e => applyProfile(e.target.value)} disabled={!!editing}>
                  {Object.entries(TRUNK_PROFILES).map(([k, p]) => (
                    <option key={k} value={k}>{p.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Auth Mode" hint="IP-based = no username/password (identify by IP)">
                <select style={S.select} value={form.auth_mode}
                  onChange={e => setForm({ ...form, auth_mode: e.target.value })}>
                  <option value="register">Registration / userpass</option>
                  <option value="ip">IP-based (no auth)</option>
                </select>
              </Field>
              <Field label="Trunk Name *">
                <input style={S.input} placeholder="pstn-trunk"
                  value={form.trunk_name} disabled={!!editing}
                  onChange={e => setForm({ ...form, trunk_name: e.target.value })} />
              </Field>
              <Field label="Display Name">
                <input style={S.input} placeholder="PSTN Trunk"
                  value={form.display_name}
                  onChange={e => setForm({ ...form, display_name: e.target.value })} />
              </Field>
              <Field label="Transport Protocol">
                <select style={S.select}
                  value={form.transport_protocol}
                  onChange={e => setForm({ ...form, transport_protocol: e.target.value })}>
                  <option value="udp">UDP</option>
                  <option value="tcp">TCP</option>
                  <option value="tls">TLS</option>
                </select>
              </Field>
              <Field label="SIP Host *" hint="IP address or hostname of the trunk provider">
                <input style={S.input} placeholder="sip.provider.com or 10.0.0.4"
                  value={form.sip_host}
                  onChange={e => setForm({ ...form, sip_host: e.target.value })} />
              </Field>
              <Field label="SIP Port">
                <input style={S.input} type="number" placeholder={isTls ? '5061' : '5060'}
                  value={form.sip_port}
                  onChange={e => setForm({ ...form, sip_port: e.target.value })} />
              </Field>
              <Field label="Inbound Context">
                <input style={S.input} placeholder="from-trunk"
                  value={form.context}
                  onChange={e => setForm({ ...form, context: e.target.value })} />
              </Field>
              {form.auth_mode !== 'ip' && (
                <>
                  <Field label="Auth Username" hint="Leave blank for IP-based auth">
                    <input style={S.input} placeholder="username (if required)"
                      value={form.auth_username}
                      onChange={e => setForm({ ...form, auth_username: e.target.value })} />
                  </Field>
                  <Field label={editing ? 'Auth Password (blank = keep existing)' : 'Auth Password'}>
                    <input style={S.input} type="password" placeholder="SIP trunk password"
                      value={form.auth_password}
                      onChange={e => setForm({ ...form, auth_password: e.target.value })} />
                  </Field>
                </>
              )}
              <Field label="Codecs">
                <input style={S.input} placeholder="ulaw,alaw,g729"
                  value={form.codecs}
                  onChange={e => setForm({ ...form, codecs: e.target.value })} />
              </Field>
              <div style={{ display: 'flex', gap: 20, alignItems: 'center', gridColumn: '1 / -1', marginTop: 4 }}>
                <CheckRow label="Default outbound trunk" checked={!!form.is_primary}
                  onChange={v => setForm({ ...form, is_primary: v })} />
                <CheckRow label="Active" checked={!!form.is_active}
                  onChange={v => setForm({ ...form, is_active: v })} />
              </div>
            </div>
          )}

          {/* ── ADVANCED TAB ──────────────────────────────────────────────── */}
          {activeTab === 'advanced' && (
            <div>
              <div style={S.sectionHead}>Transport Binding</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
                <Field label="Bind Address" hint="e.g. 0.0.0.0:5060 or 46.62.155.207:5060">
                  <input style={S.input} placeholder="0.0.0.0:5060 (auto)"
                    value={opts.bind_address || ''}
                    onChange={e => setOpt('bind_address', e.target.value)} />
                </Field>
                <Field label="External Signaling Address" hint="Public IP for SIP signaling (NAT)">
                  <input style={S.input} placeholder="public.ip.address"
                    value={opts.external_signaling_address || ''}
                    onChange={e => setOpt('external_signaling_address', e.target.value)} />
                </Field>
                <Field label="External Signaling Port">
                  <input style={S.input} placeholder={isTls ? '5061' : '5060'}
                    value={opts.external_signaling_port || ''}
                    onChange={e => setOpt('external_signaling_port', e.target.value)} />
                </Field>
                <Field label="External Media Address" hint="Public IP for RTP (NAT)">
                  <input style={S.input} placeholder="public.ip.address"
                    value={opts.external_media_address || ''}
                    onChange={e => setOpt('external_media_address', e.target.value)} />
                </Field>
                <Field label="Local Networks (NAT bypass)" hint="Comma-separated CIDRs, e.g. 192.168.0.0/16,172.24.0.0/16" >
                  <input style={S.input} placeholder="192.168.0.0/16"
                    value={opts.local_net || ''}
                    onChange={e => setOpt('local_net', e.target.value)} />
                </Field>
              </div>

              {isTls && (
                <>
                  <div style={S.sectionHead}>TLS Settings</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
                    <Field label="TLS Method">
                      <select style={S.select} value={opts.tls_method || 'tlsv1_2'}
                        onChange={e => setOpt('tls_method', e.target.value)}>
                        <option value="tlsv1_2">TLSv1.2</option>
                        <option value="tlsv1_3">TLSv1.3</option>
                        <option value="tlsv1">TLSv1 (legacy)</option>
                        <option value="sslv23">SSLv23 (auto)</option>
                      </select>
                    </Field>
                    <Field label="TLS Bind Port" hint="Port for TLS transport (default 5061)">
                      <input style={S.input} placeholder="5061"
                        value={opts.tls_port || ''}
                        onChange={e => setOpt('tls_port', e.target.value)} />
                    </Field>
                    <Field label="Certificate File">
                      <input style={S.input} placeholder="Path to TLS certificate"
                        value={opts.tls_cert_file || ''}
                        onChange={e => setOpt('tls_cert_file', e.target.value)} />
                    </Field>
                    <Field label="Private Key File">
                      <input style={S.input} placeholder="Path to TLS private key"
                        value={opts.tls_priv_key_file || ''}
                        onChange={e => setOpt('tls_priv_key_file', e.target.value)} />
                    </Field>
                    <Field label="CA List File">
                      <input style={S.input} placeholder="/etc/ssl/certs/ca-certificates.crt"
                        value={opts.tls_ca_list_file || ''}
                        onChange={e => setOpt('tls_ca_list_file', e.target.value)} />
                    </Field>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <CheckRow label="Verify Server Certificate" checked={opts.tls_verify_server !== false}
                        onChange={v => setOpt('tls_verify_server', v)} />
                      <CheckRow label="Require Client Certificate" checked={!!opts.tls_verify_client}
                        onChange={v => setOpt('tls_verify_client', v)} />
                    </div>
                  </div>
                </>
              )}

              <div style={S.sectionHead}>Endpoint Options</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
                <Field label="DTMF Mode">
                  <select style={S.select} value={opts.dtmf_mode || 'rfc4733'}
                    onChange={e => setOpt('dtmf_mode', e.target.value)}>
                    <option value="rfc4733">RFC 4733 (RTP)</option>
                    <option value="inband">In-band</option>
                    <option value="info">SIP INFO</option>
                    <option value="auto">Auto</option>
                  </select>
                </Field>
                <Field label="Media Encryption">
                  <select style={S.select} value={opts.media_encryption || 'no'}
                    onChange={e => setOpt('media_encryption', e.target.value)}>
                    <option value="no">None</option>
                    <option value="sdes">SDES (SRTP)</option>
                    <option value="dtls">DTLS-SRTP</option>
                  </select>
                </Field>
                <Field label="Outbound Proxy" hint="e.g. Teams: sip:sip.pstnhub.microsoft.com;lr">
                  <input style={S.input} placeholder="sip:proxy.example.com;lr"
                    value={opts.outbound_proxy || ''}
                    onChange={e => setOpt('outbound_proxy', e.target.value)} />
                </Field>
                <Field label="From User" hint="Override caller-ID username (from_user)">
                  <input style={S.input} placeholder="e.g. 1006"
                    value={opts.from_user || ''}
                    onChange={e => setOpt('from_user', e.target.value)} />
                </Field>
                <Field label="Session Timers">
                  <select style={S.select} value={opts.timers || ''}
                    onChange={e => setOpt('timers', e.target.value)}>
                    <option value="">Default (yes)</option>
                    <option value="yes">Yes</option>
                    <option value="required">Required</option>
                    <option value="no">No</option>
                  </select>
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, gridColumn: '1 / -1' }}>
                  <CheckRow label="RTP Symmetric" checked={opts.rtp_symmetric !== false}
                    onChange={v => setOpt('rtp_symmetric', v)} hint="Recommended for NAT" />
                  <CheckRow label="Force rport" checked={opts.force_rport !== false}
                    onChange={v => setOpt('force_rport', v)} hint="Recommended for NAT" />
                  <CheckRow label="Rewrite Contact" checked={opts.rewrite_contact !== false}
                    onChange={v => setOpt('rewrite_contact', v)} />
                  <CheckRow label="Direct Media" checked={!!opts.direct_media}
                    onChange={v => setOpt('direct_media', v)} hint="Disable for NAT/proxy" />
                  <CheckRow label="Send PAI" checked={!!opts.send_pai}
                    onChange={v => setOpt('send_pai', v)} hint="P-Asserted-Identity" />
                  <CheckRow label="Send RPID" checked={!!opts.send_rpid}
                    onChange={v => setOpt('send_rpid', v)} hint="Remote-Party-ID" />
                  <CheckRow label="Trust ID Inbound" checked={!!opts.trust_id_inbound}
                    onChange={v => setOpt('trust_id_inbound', v)} />
                  <CheckRow label="Trust ID Outbound" checked={!!opts.trust_id_outbound}
                    onChange={v => setOpt('trust_id_outbound', v)} />
                </div>
              </div>

              <div style={S.sectionHead}>AOR / Registration</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
                <Field label="Qualify Frequency (sec)" hint="0 = disable OPTIONS keepalive">
                  <input style={S.input} type="number" placeholder="30"
                    value={opts.qualify_frequency ?? '30'}
                    onChange={e => setOpt('qualify_frequency', e.target.value)} />
                </Field>
                <Field label="Max Contacts">
                  <input style={S.input} type="number" placeholder="1"
                    value={opts.max_contacts ?? '1'}
                    onChange={e => setOpt('max_contacts', e.target.value)} />
                </Field>
                <Field label="Extra Contacts" hint="One contact URI per line (e.g. Teams backup hubs)">
                  <textarea style={{ ...S.input, height: 72, resize: 'vertical' } as any}
                    placeholder="sip:sip2.pstnhub.microsoft.com:5061"
                    value={opts.extra_contacts || ''}
                    onChange={e => setOpt('extra_contacts', e.target.value)} />
                </Field>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 18 }}>
                  <CheckRow label="Remove Existing Contacts" checked={opts.remove_existing !== false}
                    onChange={v => setOpt('remove_existing', v)} />
                </div>
              </div>

              <div style={S.sectionHead}>IP-Based Identification</div>
              <Field label="Match IPs / CIDRs" hint="Comma-separated — leave blank to match by SIP host. No username/password needed when using IP auth.">
                <input style={S.input} placeholder="52.112.0.0/14, 52.120.0.0/14"
                  value={opts.identify_by_ip || ''}
                  onChange={e => setOpt('identify_by_ip', e.target.value)} />
              </Field>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button className="admin-btn primary" onClick={save}>
              {editing ? 'Update Trunk' : 'Create Trunk'}
            </button>
            <button className="admin-btn" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Trunk list ─────────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ color: '#94a3b8', padding: 20 }}>Loading trunks…</div>
      ) : trunks.length === 0 ? (
        <div style={{ color: '#94a3b8', padding: 20 }}>
          {readOnly ? 'No trunks are assigned to your organisation yet.' : 'No trunks configured yet.'}
        </div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Profile</th>
              <th>Protocol</th>
              <th>Host</th>
              <th>Port</th>
              {!readOnly && <th>Auth</th>}
              <th>Codecs</th>
              {!readOnly && <th>Tenants</th>}
              <th>Status</th>
              {!readOnly && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {trunks.map(t => (
              <tr key={t.id}>
                <td>
                  <strong style={{ color: '#e2e8f0' }}>{t.trunk_name}</strong>
                  {t.display_name && t.display_name !== t.trunk_name && (
                    <div style={{ fontSize: 11, color: '#64748b' }}>{t.display_name}</div>
                  )}
                  {t.is_primary ? <div style={{ fontSize: 10, color: '#10b981', fontWeight: 600 }}>✓ Default</div> : null}
                  {isSuperAdmin && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, marginTop: 3, fontWeight: 600, color: (t as any).super_admin_only ? '#f59e0b' : '#64748b' }}>
                      <input type="checkbox" checked={!!Number((t as any).super_admin_only)} onChange={(e) => toggleRestricted(t, e.target.checked)} />
                      Super admin only
                    </label>
                  )}
                </td>
                <td style={{ fontSize: 11, color: '#c4b5fd' }}>
                  {TRUNK_PROFILES[t.trunk_profile || 'generic']?.label || t.trunk_profile || 'Generic SIP'}
                </td>
                <td>
                  <span style={{
                    background: t.transport_protocol === 'tls' ? 'rgba(16,185,129,0.15)'
                      : t.transport_protocol === 'tcp' ? 'rgba(59,130,246,0.15)' : 'rgba(100,116,139,0.15)',
                    color: t.transport_protocol === 'tls' ? '#6ee7b7'
                      : t.transport_protocol === 'tcp' ? '#93c5fd' : '#94a3b8',
                    borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 700,
                    textTransform: 'uppercase',
                  }}>
                    {(t.transport_protocol || 'udp').toUpperCase()}
                  </span>
                </td>
                <td style={{ color: '#cbd5e1' }}>{t.sip_host}</td>
                <td style={{ color: '#94a3b8' }}>{t.sip_port}</td>
                {!readOnly && (
                  <td style={{ color: '#94a3b8', fontSize: 12 }}>
                    {t.auth_username ? `${t.auth_username} / ••••` : <span style={{ color: '#475569' }}>IP-auth</span>}
                  </td>
                )}
                <td style={{ fontSize: 11, color: '#94a3b8' }}>{t.codecs}</td>
                {!readOnly && (
                  <td style={{ fontSize: 11 }}>
                    {(t.assigned_tenant_ids && t.assigned_tenant_ids.length > 0)
                      ? <span style={{ color: '#93c5fd' }}>{t.assigned_tenant_ids.map(id => tenantById(id) ? tenantName(tenantById(id)!) : `#${id}`).join(', ')}</span>
                      : <span style={{ color: '#64748b' }}>Unassigned</span>}
                  </td>
                )}
                <td>
                  <span style={{ fontSize: 12, fontWeight: 700, color: t.is_active ? '#10b981' : '#ef4444' }}>
                    {t.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                {!readOnly && (
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="admin-btn sm" onClick={() => openAssign(t)}>Assign</button>
                    {' '}
                    <button className="admin-btn sm" onClick={() => startEdit(t)}>Edit</button>
                    {' '}
                    <button className="admin-btn sm danger" onClick={() => remove(t)}>Delete</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ── Tenant assignment modal (super admin) ──────────────────────────── */}
      {!readOnly && assignFor && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => !savingAssign && setAssignFor(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#0f172a', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 12,
            padding: 22, width: 440, maxHeight: '80vh', overflow: 'auto',
          }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#f1f5f9', marginBottom: 4 }}>
              Assign trunk to tenants
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
              <strong style={{ color: '#c4b5fd' }}>{assignFor.trunk_name}</strong> — select which tenants may route calls over this trunk.
            </div>
            {tenants.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 13 }}>No tenants found.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
                {tenants.map(tn => (
                  <label key={tn.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                    padding: '7px 10px', borderRadius: 6,
                    background: assignIds.includes(tn.id) ? 'rgba(139,92,246,0.12)' : 'rgba(15,23,42,0.5)',
                    border: `1px solid ${assignIds.includes(tn.id) ? 'rgba(139,92,246,0.4)' : 'rgba(100,116,139,0.2)'}` }}>
                    <input type="checkbox" checked={assignIds.includes(tn.id)}
                      onChange={() => toggleAssign(tn.id)} style={{ accentColor: '#8b5cf6' }} />
                    <span style={{ fontSize: 13, color: '#e2e8f0' }}>{tenantName(tn)}</span>
                    <span style={{ fontSize: 10, color: '#475569', marginLeft: 'auto' }}>#{tn.id}</span>
                  </label>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="admin-btn primary" onClick={saveAssign} disabled={savingAssign}>
                {savingAssign ? 'Saving…' : `Save (${assignIds.length})`}
              </button>
              <button className="admin-btn" onClick={() => setAssignFor(null)} disabled={savingAssign}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
